import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import ServicePricingSection from "@/components/services/ServicePricingSection";
import ServicePaymentOverrideSection from "@/components/services/ServicePaymentOverrideSection";
import { SERVICES, INVENTORY, FOLLOWUPS } from "@/constants/testIds";
import { PRICE_TYPE_OPTIONS, SERVICE_CATEGORIES, SERVICE_KIND_OPTIONS } from "@/lib/pricingUtils";

const EMPTY_FORM = {
  name: "", description: "", short_description: "", category: "none", duration_minutes: 30, price: 0, price_type: "fijo", price_note: "",
  photo_url: null, professional_ids: [], recipe: [], effect_duration_value: "", effect_duration_unit: "none",
  review_included_days: "", suggested_zone_ids: [],
  service_kind: "treatment",
  show_duration: true,
  promo_enabled: false, promo_price: "", promo_label: "", promo_start_date: "", promo_end_date: "",
  payment_mode_override: "inherit", deposit_type_override: "percentage", deposit_value_override: "",
};
const SERVICE_PLACEHOLDER = "https://images.unsplash.com/photo-1552693673-1bf958298935?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function ServiceModal({ open, onOpenChange, service, professionals, products, zones, onSubmit }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(service);
  const isTreatment = form.service_kind === "treatment";

  useEffect(() => {
    if (service) {
      setForm({
        name: service.name || "",
        description: service.description || "",
        short_description: service.short_description || "",
        category: service.category || "none",
        duration_minutes: service.duration_minutes || 30,
        price: service.price ?? 0,
        price_type: service.price_type || "fijo",
        price_note: service.price_note || "",
        photo_url: service.photo_url || null,
        professional_ids: service.professional_ids || [],
        recipe: service.recipe || [],
        effect_duration_value: service.effect_duration_value ?? "",
        effect_duration_unit: service.effect_duration_unit || "none",
        review_included_days: service.review_included_days ?? "",
        suggested_zone_ids: service.suggested_zone_ids || [],
        service_kind: service.service_kind || "treatment",
        show_duration: service.show_duration ?? true,
        promo_enabled: service.promo_enabled ?? false,
        promo_price: service.promo_price ?? "",
        promo_label: service.promo_label || "",
        promo_start_date: service.promo_start_date || "",
        promo_end_date: service.promo_end_date || "",
        payment_mode_override: service.payment_mode_override || "inherit",
        deposit_type_override: service.deposit_type_override || "percentage",
        deposit_value_override: service.deposit_value_override ?? "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [service, open]);

  const toggleProfessional = (id) => {
    setForm((f) => ({
      ...f,
      professional_ids: f.professional_ids.includes(id)
        ? f.professional_ids.filter((p) => p !== id)
        : [...f.professional_ids, id],
    }));
  };

  const toggleSuggestedZone = (id) => {
    setForm((f) => ({
      ...f,
      suggested_zone_ids: f.suggested_zone_ids.includes(id)
        ? f.suggested_zone_ids.filter((z) => z !== id)
        : [...f.suggested_zone_ids, id],
    }));
  };

  const addRecipeRow = () => {
    setForm((f) => ({ ...f, recipe: [...f.recipe, { product_id: products[0]?.id || "", quantity: 1 }] }));
  };

  const updateRecipeRow = (index, patch) => {
    setForm((f) => ({ ...f, recipe: f.recipe.map((r, i) => (i === index ? { ...r, ...patch } : r)) }));
  };

  const removeRecipeRow = (index) => {
    setForm((f) => ({ ...f, recipe: f.recipe.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const hasEffectDuration = isTreatment && form.effect_duration_unit !== "none" && form.effect_duration_value !== "";
      await onSubmit(
        {
          ...form,
          category: form.category === "none" ? null : form.category,
          duration_minutes: Number(form.duration_minutes),
          price: Number(form.price),
          price_note: form.price_note || null,
          short_description: form.short_description || null,
          recipe: isTreatment ? form.recipe.filter((r) => r.product_id).map((r) => ({ product_id: r.product_id, quantity: Number(r.quantity) || 1 })) : [],
          effect_duration_value: hasEffectDuration ? Number(form.effect_duration_value) : null,
          effect_duration_unit: hasEffectDuration ? form.effect_duration_unit : null,
          review_included_days: isTreatment && form.review_included_days !== "" ? Number(form.review_included_days) : null,
          suggested_zone_ids: isTreatment ? form.suggested_zone_ids : [],
          promo_enabled: form.promo_enabled,
          promo_price: form.promo_enabled && form.promo_price !== "" ? Number(form.promo_price) : null,
          promo_label: form.promo_enabled && form.promo_label ? form.promo_label : null,
          promo_start_date: form.promo_enabled && form.promo_start_date ? form.promo_start_date : null,
          promo_end_date: form.promo_enabled && form.promo_end_date ? form.promo_end_date : null,
          payment_mode_override: form.payment_mode_override === "inherit" ? null : form.payment_mode_override,
          deposit_type_override: form.payment_mode_override === "deposit" ? form.deposit_type_override : null,
          deposit_value_override: form.payment_mode_override === "deposit" && form.deposit_value_override !== "" ? Number(form.deposit_value_override) : null,
        },
        service
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar servicio" : "Nuevo servicio"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ImageUpload
            currentUrl={form.photo_url}
            onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
            placeholderUrl={SERVICE_PLACEHOLDER}
            testId={SERVICES.modalPhotoUpload}
            shape="square"
          />

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Tipo de cita</Label>
            <Select value={form.service_kind} onValueChange={(v) => setForm((f) => ({ ...f, service_kind: v }))}>
              <SelectTrigger data-testid={SERVICES.modalKindSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                {SERVICE_KIND_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {!isTreatment && (
              <p className="text-xs text-muted-foreground mt-1">
                No consume inventario ni genera seguimiento, y no aparece en el ranking de servicios más vendidos.
              </p>
            )}
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre del servicio</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid={SERVICES.modalNameInput} placeholder="Ej: Toxina Botulinica" />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Descripción</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              data-testid={SERVICES.modalDescriptionInput}
              placeholder="Detalle del tratamiento (podés incluir ¿Qué puedes esperar?, Áreas tratadas, Preguntas frecuentes, etc. con saltos de línea)"
              rows={7}
              className="whitespace-pre-line"
            />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Descripción corta (para la landing pública)</Label>
            <Input
              value={form.short_description}
              onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))}
              data-testid={SERVICES.modalShortDescriptionInput}
              placeholder="1-2 líneas, máx. 140 caracteres. Si la dejás vacía, se usa un resumen automático de la descripción."
              maxLength={140}
            />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Categoría</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid={SERVICES.modalCategorySelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin categoría</SelectItem>
                {SERVICE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Duracion (minutos)</Label>
              <Input type="number" min="5" value={form.duration_minutes} onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))} data-testid={SERVICES.modalDurationInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Precio (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid={SERVICES.modalPriceInput} />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <Switch checked={form.show_duration} onCheckedChange={(v) => setForm((f) => ({ ...f, show_duration: v }))} data-testid={SERVICES.modalShowDurationToggle} />
            <span className="text-sm text-foreground">Mostrar la duración en la web</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-1">
            La duración se sigue usando para calcular la agenda aunque no se muestre en la web pública.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Tipo de precio</Label>
              <Select value={form.price_type} onValueChange={(v) => setForm((f) => ({ ...f, price_type: v }))}>
                <SelectTrigger data-testid={SERVICES.modalPriceTypeSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICE_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Nota de precio (opcional)</Label>
              <Input
                value={form.price_note}
                onChange={(e) => setForm((f) => ({ ...f, price_note: e.target.value }))}
                data-testid={SERVICES.modalPriceNoteInput}
                placeholder="Ej: Incluye microdermabrasión"
              />
            </div>
          </div>

          <ServicePricingSection form={form} setForm={setForm} />

          <ServicePaymentOverrideSection form={form} setForm={setForm} />

          {isTreatment && (
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Duracion del efecto (para el Seguimiento)</Label>
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number" min="1"
                placeholder="Ej: 4"
                value={form.effect_duration_value}
                onChange={(e) => setForm((f) => ({ ...f, effect_duration_value: e.target.value }))}
                disabled={form.effect_duration_unit === "none"}
                data-testid={FOLLOWUPS.serviceEffectDurationValueInput}
              />
              <Select value={form.effect_duration_unit} onValueChange={(v) => setForm((f) => ({ ...f, effect_duration_unit: v }))}>
                <SelectTrigger data-testid={FOLLOWUPS.serviceEffectDurationUnitSelect}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin seguimiento</SelectItem>
                  <SelectItem value="weeks">Semanas</SelectItem>
                  <SelectItem value="months">Meses</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Se usa para calcular automaticamente cuando el paciente deberia volver, en el panel de Seguimiento.
            </p>
          </div>
          )}

          {isTreatment && (
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Revisión incluida sin cargo (días)</Label>
            <Input
              type="number" min="0"
              placeholder="Ej: 15 (dejar vacío si no aplica)"
              value={form.review_included_days}
              onChange={(e) => setForm((f) => ({ ...f, review_included_days: e.target.value }))}
              data-testid={SERVICES.reviewIncludedDaysInput}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Días desde la aplicación en los que la paciente puede volver para un retoque sin cargo (ficha técnica).
            </p>
          </div>
          )}

          {isTreatment && (
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Zonas de aplicación sugeridas</Label>
            <div className="border border-border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
              {(!zones || zones.length === 0) && <p className="text-sm text-muted-foreground">No hay zonas cargadas en Configuración todavía.</p>}
              {zones?.map((z) => (
                <label key={z.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.suggested_zone_ids.includes(z.id)}
                    onCheckedChange={() => toggleSuggestedZone(z.id)}
                    data-testid={`${SERVICES.modalSuggestedZoneCheckboxPrefix}${z.id}`}
                  />
                  {z.name}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Se muestran como acceso rápido al registrar zonas del tratamiento.
            </p>
          </div>
          )}

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Profesionales que lo realizan</Label>
            <div className="border border-border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
              {professionals.length === 0 && <p className="text-sm text-muted-foreground">No hay profesionales cargados aun.</p>}
              {professionals.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.professional_ids.includes(p.id)}
                    onCheckedChange={() => toggleProfessional(p.id)}
                    data-testid={`${SERVICES.modalProfessionalCheckboxPrefix}${p.id}`}
                  />
                  {p.name} {p.specialty ? `- ${p.specialty}` : ""}
                </label>
              ))}
            </div>
          </div>

          {isTreatment && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm text-muted-foreground block">Insumos estandar (receta) &middot; usados para calcular el margen</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addRecipeRow} disabled={products.length === 0} data-testid={INVENTORY.recipeAddButton}>
                <Plus className="w-3.5 h-3.5" /> Agregar insumo
              </Button>
            </div>
            {products.length === 0 ? (
              <p className="text-sm text-muted-foreground">Cargá productos en Inventario para poder asociarlos.</p>
            ) : form.recipe.length === 0 ? (
              <p className="text-sm text-muted-foreground">Este servicio no tiene insumos estandar asociados.</p>
            ) : (
              <div className="space-y-2">
                {form.recipe.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`${INVENTORY.recipeRowPrefix}${idx}`}>
                    <Select value={row.product_id} onValueChange={(v) => updateRecipeRow(idx, { product_id: v })}>
                      <SelectTrigger className="flex-1" data-testid={`${INVENTORY.recipeProductSelectPrefix}${idx}`}><SelectValue placeholder="Producto" /></SelectTrigger>
                      <SelectContent>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number" min="0" step="0.01" value={row.quantity} className="w-24"
                      onChange={(e) => updateRecipeRow(idx, { quantity: e.target.value })}
                      data-testid={`${INVENTORY.recipeQuantityInputPrefix}${idx}`}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeRecipeRow(idx)} data-testid={`${INVENTORY.recipeRemoveButtonPrefix}${idx}`}>
                      <X className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={SERVICES.modalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={SERVICES.modalSaveButton}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
